import { forwardRef, type HTMLAttributes, type ReactNode } from 'react'
import { cn } from '@/lib/utils'
import { DsSkeleton } from '@/ds/skeleton'

export type DsCardVariant = 'surface' | 'outline' | 'muted' | 'elevated' | 'brand'

const variantClass: Record<DsCardVariant, string> = {
  surface: 'border bg-card text-card-foreground',
  outline: 'border bg-transparent text-foreground',
  muted: 'bg-muted text-foreground',
  elevated: 'bg-card text-card-foreground shadow-sm',
  brand: 'bg-[image:var(--brand-gradient)] text-white [&_*]:text-white',
}

export interface DsCardProps extends HTMLAttributes<HTMLDivElement> {
  variant?: DsCardVariant
  /** Default 16 px; `none` when composing with header/content/footer sections. */
  padding?: 'none' | 'sm' | 'md' | 'lg'
  onClick?: () => void
  loading?: boolean
  semanticLabel?: string
  children?: ReactNode
}

const paddingClass = { none: '', sm: 'p-3', md: 'p-4', lg: 'p-6' }

/** Borders separate surfaces; only `elevated` carries a shadow. */
export const DsCard = forwardRef<HTMLDivElement, DsCardProps>(function DsCard({ variant = 'surface', padding = 'md', onClick, loading, semanticLabel, className, children, ...rest }, ref) {
  const interactive = !!onClick
  return (
    <div
      ref={ref}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      aria-label={semanticLabel}
      aria-busy={loading || undefined}
      onClick={onClick}
      onKeyDown={(e) => interactive && (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), onClick?.())}
      className={cn('rounded-xl outline-none', variantClass[variant], paddingClass[padding], interactive && 'cursor-pointer transition-[background-color,box-shadow] motion-fast hover:brightness-[0.98] focus-visible:ring-focus dark:hover:brightness-110', className)}
      {...rest}
    >
      {loading ? (
        <div className="space-y-3">
          <DsSkeleton width="50%" height={14} />
          <DsSkeleton width="80%" height={12} />
          <DsSkeleton width="30%" height={12} />
        </div>
      ) : (
        children
      )}
    </div>
  )
})

export interface DsCardHeaderProps {
  title?: ReactNode
  description?: ReactNode
  leading?: ReactNode
  trailing?: ReactNode
  className?: string
}

export function DsCardHeader({ title, description, leading, trailing, className }: DsCardHeaderProps) {
  return (
    <div className={cn('flex items-start gap-3 px-4 pb-2 pt-4', className)}>
      {leading}
      <div className="min-w-0 flex-1">
        {title && <h3 className="text-title3 text-foreground">{title}</h3>}
        {description && <p className="mt-0.5 text-small text-muted-foreground">{description}</p>}
      </div>
      {trailing}
    </div>
  )
}

export const DsCardContent = ({ children, className }: { children: ReactNode; className?: string }) => <div className={cn('px-4 pb-4', className)}>{children}</div>
export const DsCardFooter = ({ children, className }: { children: ReactNode; className?: string }) => <div className={cn('px-4 pb-4', className)}>{children}</div>

export function DsCardSections({ children, dividers, ...props }: DsCardProps & { dividers?: boolean }) {
  return (
    <DsCard padding="none" {...props} className={cn(dividers && '[&>*+*]:border-t', props.className)}>
      {children}
    </DsCard>
  )
}
